import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import Spinner from "../components/Spinner";
import { SERVER_BASE_URL } from "../config";

function SetNewPasswordPage() {
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get("token");

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage("");
        setError("");

        if (password.length < 6) {
            setError("Password must be at least 6 characters long.");
            return;
        }

        if (password !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(`${SERVER_BASE_URL}/api/users/reset-password?token=${token}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
                body: new URLSearchParams({ password }),
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.detail || "Failed to reset password.");
            }

            setMessage("Your password has been successfully reset!");
            setTimeout(() => {
                navigate("/login");
            }, 2000);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
            <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 w-full max-w-md mx-4">
                <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-800">Set New Password</h2>
                    <p className="text-gray-600 mt-1">Create a new password for your account</p>
                </div>
                
                {message && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-md mb-6">
                        <p className="text-green-700 text-center">{message}</p>
                        <p className="text-sm text-green-600 text-center mt-2">
                            Redirecting to login page...
                        </p>
                    </div>
                )}
                
                {error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-md mb-4">
                        <p className="text-sm text-red-600 text-center">{error}</p>
                    </div>
                )}
                
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            New Password
                        </label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="Enter new password"
                            required
                        />
                        <p className="mt-1 text-xs text-gray-500">
                            Password must be at least 6 characters long
                        </p>
                    </div>
                    
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Confirm Password
                        </label>
                        <input
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="Confirm new password"
                            required
                        />
                    </div>
                    
                    <button
                        type="submit"
                        className={`w-full bg-indigo-600 text-white py-2 px-4 rounded-md font-medium flex items-center justify-center ${
                            loading ? "opacity-70 cursor-not-allowed" : "hover:bg-indigo-700"
                        } transition duration-200`}
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <Spinner className="w-5 h-5 mr-2" />
                                <span>Updating Password...</span>
                            </>
                        ) : (
                            "Set New Password"
                        )}
                    </button>
                    
                    {!message && (
                        <div className="mt-4 text-center">
                            <p className="text-sm text-gray-600">
                                Remember your password?{" "}
                                <Link to="/login" className="text-indigo-600 hover:text-indigo-800 font-medium">
                                    Sign in
                                </Link>
                            </p>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
}

export default SetNewPasswordPage;